import type { FormEvent } from "react";
import {
  AlertCircle,
  ExternalLink,
  Globe,
  LayoutTemplate,
  Loader2,
  Palette,
  Plus,
  Sparkles,
  Wrench,
} from "lucide-react";
import type { PageSummary } from "@/hooks/use-pages";
import { formatThemeStatus, formatShellStatus, type SiteRecord } from "./sites-utils";

// ---------------------------------------------------------------------------
// Site Manager Panel
// ---------------------------------------------------------------------------

function formatScheduledPublishTime(isoValue: string): string {
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) {
    return "Invalid scheduled time";
  }

  return date.toLocaleString();
}

export interface SiteManagerPanelProps {
  sites: SiteRecord[];
  pages: Array<{ id: string; title: string; status: string; siteId: string | null }>;
  isLoading: boolean;
  selectedSiteId: string | null;
  onSelectSite: (siteId: string) => void;
  onCreateSite: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  createError: string | null;
  isCreatingSite: boolean;
  newSiteName: string;
  newSiteSlug: string;
  onNameChange: (value: string) => void;
  onSlugChange: (value: string) => void;
  onOpenSettings: (siteId: string) => void;
  onEditShell: (siteId: string, shell: "header" | "footer") => void;
  onEditNavigation: (siteId: string) => void;
  onCreateWithAi: (siteId: string) => void;
  onBuildBlank: (siteId: string) => void;
  onPreviewSite: (siteId: string) => void;
  onPublishSite: (siteId: string) => void;
  publishStates: Record<string, {
    target: "preview" | "live";
    status: "queued" | "running" | "completed" | "partial_failure" | "failed" | null;
    jobId: string | null;
    totalPages: number;
    completedPages: number;
    failedPages: number;
    isSubmitting: boolean;
    error: string | null;
    liveUrl: string | null;
    servePath: string | null;
    previewUrl: string | null;
    scheduledPublishGap: string | null;
    serving: {
      storage: "r2";
      cdn: "cloudflare_worker";
      worker: "page-serve";
      liveRoute: string;
      previewRoute: string;
      r2Prefix: string;
      sitemapKey: string;
      robotsTxtKey: string;
    } | null;
    customDomain: {
      domain: string;
      status: string;
      sslStatus: string | null;
      dnsResolved: boolean | null;
      readyForRouting: boolean;
      healthEndpoint: string;
      verifyEndpoint: string;
    } | null;
  }>;
}

export function SiteManagerPanel({
  sites,
  pages,
  isLoading,
  selectedSiteId,
  onSelectSite,
  onCreateSite,
  createError,
  isCreatingSite,
  newSiteName,
  newSiteSlug,
  onNameChange,
  onSlugChange,
  onOpenSettings,
  onEditShell,
  onEditNavigation,
  onCreateWithAi,
  onBuildBlank,
  onPreviewSite,
  onPublishSite,
  publishStates,
}: SiteManagerPanelProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading site workspace...
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <form onSubmit={(event) => void onCreateSite(event)} className="rounded-[22px] border border-border bg-background p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label htmlFor="site-name" className="block text-sm font-medium text-foreground">
              Site name
            </label>
            <input
              id="site-name"
              value={newSiteName}
              onChange={(event) => onNameChange(event.target.value)}
              placeholder="Main marketing site"
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
            />
          </div>
          <div className="flex-1">
            <label htmlFor="site-slug" className="block text-sm font-medium text-foreground">
              Site slug
            </label>
            <input
              id="site-slug"
              value={newSiteSlug}
              onChange={(event) => onSlugChange(event.target.value)}
              placeholder="main-site"
              className="mt-1 w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={isCreatingSite || !newSiteName.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {isCreatingSite ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Create site
          </button>
        </div>

        {createError ? (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{createError}</span>
          </div>
        ) : null}
      </form>

      {sites.length === 0 ? (
        <div className="rounded-[24px] border-2 border-dashed border-border bg-card px-6 py-10 text-center">
          <Globe className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <h3 className="mt-4 text-lg font-semibold text-foreground">Create a site container first</h3>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            The editor can still open without a site, but non-technical users need a site shell for home-page assignment, global theme tokens, and publishing settings.
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {sites.map((site) => {
            const linkedPages = pages.filter((page) => page.siteId === site.id && page.status !== "archived").length;
            const isSelected = site.id === selectedSiteId;
            const publishState = publishStates[site.id];
            const isBusy = publishState?.isSubmitting || publishState?.status === "queued" || publishState?.status === "running";
            const statusLabel = publishState
              ? publishState.target === "preview"
                ? "Preview"
                : "Live publish"
              : null;

            return (
              <article
                key={site.id}
                className={`rounded-[22px] border p-5 shadow-sm transition ${
                  isSelected ? "border-primary/30 bg-primary/5" : "border-border bg-card"
                }`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => onSelectSite(site.id)}
                      className="text-left"
                    >
                      <p className="text-lg font-semibold text-foreground">{site.name}</p>
                      <p className="text-sm text-muted-foreground">/{site.slug} · {site.siteType}</p>
                    </button>

                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="rounded-full bg-primary/10 px-3 py-1 font-medium text-primary">
                        {site.status}
                      </span>
                      <span className="rounded-full bg-muted px-3 py-1 font-medium text-muted-foreground">
                        {linkedPages} drafts in workspace
                      </span>
                    </div>

                    <p className="text-sm leading-6 text-muted-foreground">
                      {formatThemeStatus(site.globalTheme)}
                    </p>
                    <p className="text-sm leading-6 text-muted-foreground">
                      {formatShellStatus(site)}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => onPreviewSite(site.id)}
                      disabled={Boolean(isBusy)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-60"
                    >
                      {publishState?.isSubmitting && publishState.target === "preview"
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Globe className="h-4 w-4" />}
                      Preview site
                    </button>
                    <button
                      type="button"
                      onClick={() => onPublishSite(site.id)}
                      disabled={Boolean(isBusy)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent disabled:opacity-60"
                    >
                      {publishState?.isSubmitting && publishState.target === "live"
                        ? <Loader2 className="h-4 w-4 animate-spin" />
                        : <Globe className="h-4 w-4" />}
                      Publish live
                    </button>
                    <button
                      type="button"
                      onClick={() => onCreateWithAi(site.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
                    >
                      <Sparkles className="h-4 w-4" />
                      Tell me
                    </button>
                    <button
                      type="button"
                      onClick={() => onOpenSettings(site.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      <Palette className="h-4 w-4" />
                      Theme and settings
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditShell(site.id, "header")}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Global header
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditShell(site.id, "footer")}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Global footer
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditNavigation(site.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      Navigation
                    </button>
                    <button
                      type="button"
                      onClick={() => onBuildBlank(site.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-accent"
                    >
                      <Wrench className="h-4 w-4" />
                      Build blank
                    </button>
                  </div>
                </div>

                {publishState ? (
                  <div className="mt-4 rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm">
                    <div className="flex flex-wrap items-center gap-3 text-foreground">
                      <span className="font-medium">
                        {statusLabel} {publishState.status ?? "queued"}
                      </span>
                      {publishState.totalPages > 0 ? (
                        <span className="text-muted-foreground">
                          {publishState.completedPages}/{publishState.totalPages} pages completed
                          {publishState.failedPages > 0 ? ` · ${publishState.failedPages} failed` : ""}
                        </span>
                      ) : null}
                    </div>
                    {publishState.previewUrl ? (
                      <div className="mt-2">
                        <a
                          href={publishState.previewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                        >
                          Open latest preview
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    ) : null}
                    {publishState.liveUrl ? (
                      <div className="mt-2">
                        <a
                          href={publishState.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                        >
                          Open live site
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                        {publishState.servePath ? (
                          <span className="ml-3 text-xs text-muted-foreground">
                            served from {publishState.servePath}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                    {publishState.serving ? (
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">
                        Serving contract: {publishState.serving.worker} reads {publishState.serving.r2Prefix} from {publishState.serving.storage.toUpperCase()} and publishes through {publishState.serving.cdn.replace("_", " ")}.
                      </p>
                    ) : null}
                    {publishState.customDomain ? (
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">
                        Domain health: {publishState.customDomain.domain} is {publishState.customDomain.status}; DNS {publishState.customDomain.dnsResolved === null ? "unknown" : publishState.customDomain.dnsResolved ? "resolved" : "not resolved"}; TLS {publishState.customDomain.sslStatus ?? "managed/pending"}.
                        {publishState.customDomain.readyForRouting ? " Custom-domain routing is ready." : ` Verify at ${publishState.customDomain.verifyEndpoint}.`}
                      </p>
                    ) : null}
                    {publishState.scheduledPublishGap ? (
                      <p className="mt-2 text-xs leading-5 text-muted-foreground">
                        {publishState.scheduledPublishGap}
                      </p>
                    ) : null}
                    {publishState.error ? (
                      <p className="mt-2 text-xs leading-5 text-destructive">
                        {publishState.error}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page Inventory Panel
// ---------------------------------------------------------------------------

export interface PageInventoryPanelProps {
  isLoading: boolean;
  pageError: string | null;
  pages: Array<{
    id: string;
    title: string;
    slug: string;
    status: "draft" | "published" | "archived";
    siteId: string | null;
    updatedAt: string;
    views: number | null;
    publishedUrl: string | null;
    scheduledPublishAt: string | null;
    scheduledPublishVersionId: string | null;
    settings: Record<string, unknown>;
    reuseKind: "personal_template" | "page_instance" | null;
  }>;
  selectedSite: { id: string; name: string } | null;
  onRetry: () => void;
  onSaveAsTemplate: (page: PageSummary) => void;
  onCloneForCampaign: (page: PageSummary) => void;
  isApplyingToTemplate: (pageId: string) => boolean;
  isCloningForCampaign: (pageId: string) => boolean;
}

export function PageInventoryPanel({ isLoading, pageError, pages, selectedSite, onRetry, onSaveAsTemplate, onCloneForCampaign, isApplyingToTemplate, isCloningForCampaign }: PageInventoryPanelProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-[260px] items-center justify-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading page inventory...
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="flex items-start gap-3 rounded-2xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{pageError}</span>
        <button onClick={onRetry} className="ml-2 underline">Retry</button>
      </div>
    );
  }

  if (pages.length === 0) {
    return (
      <div className="rounded-[24px] border-2 border-dashed border-border bg-card px-6 py-10 text-center">
        <LayoutTemplate className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <h3 className="mt-4 text-lg font-semibold text-foreground">No pages yet</h3>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {selectedSite
            ? `No pages are attached to ${selectedSite.name} yet. Start with Tell Me or Build blank from that site card so the page inventory stays grouped under the site.`
            : "Start with Tell Me or Pick One if you want a calmer first draft. Use Build It only when you want the raw editor immediately."}
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-[22px] border border-border">
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Page</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Status</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Views</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Updated</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">Reuse</th>
          </tr>
        </thead>
        <tbody>
          {pages.map((page) => (
            <tr key={page.id} className="border-b border-border/50 last:border-0 bg-card">
              <td className="px-4 py-3">
                <p className="font-medium text-foreground">{page.title}</p>
                <p className="text-xs text-muted-foreground">/{page.slug}</p>
                {page.reuseKind === "personal_template" ? (
                  <p className="mt-1 text-xs uppercase tracking-[0.18em] text-primary">private template</p>
                ) : null}
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                <div className="space-y-1">
                  {page.scheduledPublishAt ? (
                    <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-amber-800">
                      Scheduled
                    </span>
                  ) : (
                    <p>{page.status}</p>
                  )}
                  {page.scheduledPublishAt ? (
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      Scheduled for {formatScheduledPublishTime(page.scheduledPublishAt)}
                    </p>
                  ) : null}
                </div>
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">
                {page.views === null ? "Not tracked" : page.views.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-sm text-muted-foreground">{new Date(page.updatedAt).toLocaleDateString()}</td>
              <td className="px-4 py-3">
                {page.reuseKind === "personal_template" ? (
                  <span className="text-xs text-muted-foreground">Already reusable</span>
                ) : (
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => onCloneForCampaign(page)}
                      disabled={isCloningForCampaign(page.id)}
                      className="inline-flex items-center gap-2 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
                    >
                      {isCloningForCampaign(page.id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                      Clone for campaign
                    </button>
                    <button
                      type="button"
                      onClick={() => onSaveAsTemplate(page)}
                      disabled={isApplyingToTemplate(page.id)}
                      className="inline-flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition hover:bg-accent disabled:opacity-60"
                    >
                      {isApplyingToTemplate(page.id) ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LayoutTemplate className="h-3.5 w-3.5" />}
                      Save as template
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
